/**
 * DPO Ed25519 signature on the published transcript.
 *
 * The DPO of the operating organisation (OAR Transilvania for the
 * pilot, OAR National after the consolidation) signs the canonical
 * bytes of the transcript at close-of-poll. The verifier — the
 * standalone audit app, the CLI verifier, anyone running the lib —
 * recomputes those bytes from the published JSON and checks the
 * signature against the embedded public key. The voter is expected to
 * additionally cross-check the embedded public key against the DPO's
 * officially-published key (web page, registry, magic-link email),
 * since a malicious server could forge a signature with its own key.
 *
 * STATUS: pilot consultativ. The signing key is an Ed25519 32-byte
 * secret read from the operator's environment (`OAR_DPO_ED25519_SK_HEX`
 * for the web app), generated once with `oar-vote-dpo-keygen`. The
 * production target is QTSP integration (certSIGN) which replaces this
 * function with an external signing call; the verifier path stays
 * identical because Ed25519 signatures verify the same way.
 */
import { ed25519 } from "@noble/curves/ed25519";

import type { Transcript } from "../types";
import {
  bytesToHex,
  canonicalJsonStringify,
  encodePoint,
  encodeScalar,
  hexToBytes,
} from "../internal/encoding";

export interface SignTranscriptInput {
  /** Transcript without the signature/signedAt/signedBy fields populated;
   * those are filled by `signTranscript`. `signerPublicKey` and
   * `signature` from `transcript` are ignored — they're set from
   * `signingKey` and the computed signature respectively. */
  readonly transcript: Transcript;
  readonly signingKey: Uint8Array;
  readonly signedBy: string;
  /** ISO-8601. Caller picks the timestamp so the same transcript JSON is
   * reproducible across replays. */
  readonly signedAt: string;
}

export interface SignTranscriptResult {
  readonly transcript: Transcript;
  readonly publicKey: Uint8Array;
  readonly signature: Uint8Array;
}

/**
 * Sign the transcript with an Ed25519 key. The caller supplies a
 * 32-byte secret seed; the public key is derived deterministically.
 * The returned transcript is otherwise identical to the input but with
 * `signedAt`, `signedBy`, `signerPublicKey`, and `signature` populated.
 */
export function signTranscript(input: SignTranscriptInput): SignTranscriptResult {
  if (input.signingKey.length !== 32) {
    throw new Error(
      `Ed25519 signing key must be 32 bytes, got ${input.signingKey.length}`,
    );
  }
  const publicKey = ed25519.getPublicKey(input.signingKey);
  const message = canonicalSigningBytes({
    ...input.transcript,
    signedAt: input.signedAt,
    signedBy: input.signedBy,
    signerPublicKey: bytesToHex(publicKey),
    signature: "",
  });
  const signature = ed25519.sign(message, input.signingKey);
  return {
    transcript: {
      ...input.transcript,
      signedAt: input.signedAt,
      signedBy: input.signedBy,
      signerPublicKey: bytesToHex(publicKey),
      signature: bytesToHex(signature),
    },
    publicKey,
    signature,
  };
}

export type DpoSignatureStatus =
  | { readonly kind: "absent" }
  | { readonly kind: "valid"; readonly publicKeyHex: string; readonly signedBy: string; readonly signedAt: string }
  | { readonly kind: "invalid"; readonly reason: string };

/**
 * Verify the DPO Ed25519 signature on a transcript. Returns:
 *   - `absent` when the transcript carries no signature (signature ==
 *     "" and signerPublicKey == "")
 *   - `valid` when the signature verifies against the embedded public
 *     key — caller must additionally cross-check that public key
 *     against the trusted source (the DPO's published key)
 *   - `invalid` with a human-readable reason when verification fails
 */
export function verifyTranscriptDpoSignature(
  transcript: Transcript,
): DpoSignatureStatus {
  const sig = transcript.signature;
  const pkHex = transcript.signerPublicKey;
  if (!sig && !pkHex) return { kind: "absent" };
  if (!sig) return { kind: "invalid", reason: "signerPublicKey set but signature missing" };
  if (!pkHex) return { kind: "invalid", reason: "signature set but signerPublicKey missing" };

  let pk: Uint8Array;
  let sigBytes: Uint8Array;
  try {
    pk = hexToBytes(pkHex);
    sigBytes = hexToBytes(sig);
  } catch (err) {
    return {
      kind: "invalid",
      reason: `signature/publicKey hex parse: ${err instanceof Error ? err.message : String(err)}`,
    };
  }
  if (pk.length !== 32) {
    return { kind: "invalid", reason: `signerPublicKey must be 32 bytes, got ${pk.length}` };
  }
  if (sigBytes.length !== 64) {
    return { kind: "invalid", reason: `signature must be 64 bytes, got ${sigBytes.length}` };
  }

  const message = canonicalSigningBytes({ ...transcript, signature: "" });
  let ok: boolean;
  try {
    ok = ed25519.verify(sigBytes, message, pk);
  } catch (err) {
    return {
      kind: "invalid",
      reason: `Ed25519 verify threw: ${err instanceof Error ? err.message : String(err)}`,
    };
  }
  if (!ok) {
    return { kind: "invalid", reason: "Ed25519 signature does not verify under embedded public key" };
  }
  return {
    kind: "valid",
    publicKeyHex: pkHex,
    signedBy: transcript.signedBy,
    signedAt: transcript.signedAt,
  };
}

/**
 * Generate a fresh Ed25519 signing keypair. The CLI `oar-vote-dpo-keygen`
 * is the user-facing entry point; this function is also useful in
 * tests. The caller is responsible for delivering the secret to the
 * operator out-of-band (offline keypad, sealed envelope to the DPO).
 */
export function generateDpoKey(): { secretKey: Uint8Array; publicKey: Uint8Array } {
  const { secretKey, publicKey } = ed25519.keygen();
  return { secretKey, publicKey };
}

// ---------------------------------------------------------------------------
// Canonical bytes
// ---------------------------------------------------------------------------

/**
 * Bytes the DPO signs / the verifier reproduces. The whole transcript
 * is serialized via `canonicalJsonStringify` (deterministic key order
 * and array preservation) with `signature` blanked out — the rest is
 * bound to the signature. The resulting Uint8Array is suitable for
 * `ed25519.sign` / `ed25519.verify`.
 */
function canonicalSigningBytes(transcript: Transcript): Uint8Array {
  const transcriptForSig: Record<string, unknown> = {
    ballotId: transcript.ballotId,
    title: transcript.title,
    seats: transcript.seats,
    options: transcript.options.map((o) => ({ id: o.id, label: o.label })),
    dkg: serializeDkgForSigning(transcript.dkg),
    bulletin: transcript.bulletin.map((b) => ({
      encrypted: serializeEncryptedForSigning(b.encrypted),
      receivedAt: b.receivedAt,
    })),
    tally: serializeTallyForSigning(transcript.tally),
    signedAt: transcript.signedAt,
    signedBy: transcript.signedBy,
    signerPublicKey: transcript.signerPublicKey,
    signature: "",
  };
  const json = canonicalJsonStringify(transcriptForSig);
  return new TextEncoder().encode(json);
}

function serializeEncryptedForSigning(b: Transcript["bulletin"][number]["encrypted"]): unknown {
  return {
    ballotId: b.ballotId,
    credentialPublicKey: bytesToHex(encodePoint(b.credentialPublicKey)),
    ciphertexts: b.ciphertexts.map((c) => ({
      c1: bytesToHex(encodePoint(c.c1)),
      c2: bytesToHex(encodePoint(c.c2)),
    })),
    perOptionProofs: b.perOptionProofs.map((p) => ({
      t1_0: bytesToHex(encodePoint(p.t1_0)),
      t2_0: bytesToHex(encodePoint(p.t2_0)),
      t1_1: bytesToHex(encodePoint(p.t1_1)),
      t2_1: bytesToHex(encodePoint(p.t2_1)),
      e0: bytesToHex(encodeScalar(p.e0)),
      s0: bytesToHex(encodeScalar(p.s0)),
      e1: bytesToHex(encodeScalar(p.e1)),
      s1: bytesToHex(encodeScalar(p.s1)),
    })),
    sumProof: {
      t1: bytesToHex(encodePoint(b.sumProof.t1)),
      t2: bytesToHex(encodePoint(b.sumProof.t2)),
      s: bytesToHex(encodeScalar(b.sumProof.s)),
    },
    signature: {
      R: bytesToHex(encodePoint(b.signature.R)),
      s: bytesToHex(encodeScalar(b.signature.s)),
    },
  };
}

function serializeDkgForSigning(d: Transcript["dkg"]): unknown {
  return {
    threshold: d.threshold,
    trustees: d.trustees.map((t) => ({
      id: t.id,
      index: t.index,
      name: t.name,
      verificationKey: bytesToHex(encodePoint(t.verificationKey)),
    })),
    feldmanCommitments: d.feldmanCommitments.map((row) =>
      row.map((p) => bytesToHex(encodePoint(p))),
    ),
    combinedPublicKey: bytesToHex(encodePoint(d.combinedPublicKey)),
  };
}

function serializeTallyForSigning(t: Transcript["tally"]): unknown {
  return {
    counts: [...t.counts],
    trusteeSubset: [...t.trusteeSubset],
    partialDecryptions: t.partialDecryptions.map((p) => ({
      trusteeId: p.trusteeId,
      perOption: p.perOption.map((po) => ({
        decryptionShare: bytesToHex(encodePoint(po.decryptionShare)),
        zkProof: {
          t1: bytesToHex(encodePoint(po.zkProof.t1)),
          t2: bytesToHex(encodePoint(po.zkProof.t2)),
          s: bytesToHex(encodeScalar(po.zkProof.s)),
        },
      })),
    })),
    computedAt: t.computedAt,
  };
}
